import { OrganizationTitlesListResponseDto } from 'api/dtos/organizations/titles';
import { EditAssociationFormValues } from '../../screens/associations/types/edit-association';

export type GroupMemberRole = 'member' | 'admin' | 'owner';
export type GroupMemberStatus = 'approved' | 'pending' | 'rejected';

export interface GroupDto {
  _id?: string;
  id: string;
  name: string;
  address: string;
  description: string;
  badgeImageUrl: string;
  coverImageUrl: string;
  associationDependentId?: string;
  creator: {
    id: string;
    firstName: string;
    lastName: string;
    photoUrl: string;
  };
  location?: {
    coordinates?: number[];
  };
  metadata: {
    memberCount: number;
    memberPreview: any[];
    currentUser: {
      status?: GroupMemberStatus;
      role?: GroupMemberRole;
    };
    titlesWithUsers?: OrganizationTitlesListResponseDto[];
  };
  requestToJoin: boolean;
  permissions: {
    createComments: 'member' | 'admin';
    createPosts: 'member' | 'admin';
    currentUser: {
      createPosts: boolean;
      createComments: boolean;
    };
  };
  glcode?: string;
}

export interface GroupMemberDto {
  id: string;
  groupId: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    photoUrl: string;
  };
  role: string;
  reviewedById?: string;
  status: string;
  statusChangedAt?: string;
  titles?: string[];
}

export interface GroupMembersDto {
  limit?: number;
  currentPage?: number;
  totalPages?: number;
  items: GroupMemberDto[];
}

export interface AssociationDto {
  id: string;
  description?: string;
  address: { line: string; city: string; state: string; postalCode: string };
  addressCoordinates: { lat: number; lon: number };
  country: string;
  coverImage: string;
  currentUserInfo?: {
    associationMembership?: {
      role: 'admin' | 'member';
      status: 'pending' | 'approved' | 'rejected';
    };
    associatedGroupMemberships: { groupId: string }[];
    permissions: {
      updateAssociationSettings: boolean;
      updateAssociationMembers: boolean;
      deleteAssociationMembers: boolean;
    };
  };
  logoImage: string;
  memberCount: number;
  memberPreview: {
    firstName: string;
    id: string;
    lastName: string;
    photoUrl?: string;
  }[];
  name: string;
  requestToJoin?: boolean;
  requestToPublish?: boolean;
  autoApproveRequestToPublish?: boolean;
  requestToPublishWorkflowId?: string;
  requiredEmailDomains?: string[];
  verificationStatus?: 'incomplete' | 'in_progress' | 'complete';
  screeningQuestionsFormId?: string;
  requestScreeningQuestions?: boolean;
  blockAssociationMemberJoin?: boolean;
  blockOrganizationMemberJoin?: boolean;
  memberJoinAllowedOrganizationIds?: string[];
}

export interface AssociationsDto {
  limit: number;
  currentPage: number;
  totalPages: number;
  items: AssociationDto[];
}

export interface AssociationDtoWithMetadata extends AssociationDto {
  isUserAdmin: boolean;
  isUserMember: boolean;
  isUserPending: boolean;
  isUserRejected: boolean;
}

export interface GroupsDto {
  limit: number;
  currentPage: number;
  totalPages: number;
  items: GroupDto[];
}
interface MembersDto {
  limit: number;
  currentPage: number;
  totalPages: number;
  items: GroupMemberDto[];
}
export interface Members {
  limit: number;
  currentPage: number;
  totalPages: number;
  items: MembersDto[];
}

export enum GroupMemberRoleEnum {
  MEMBER = 'member',
  ADMIN = 'admin',
  OWNER = 'owner'
}

export interface GroupWithMetadata extends GroupDto {
  isUserAdmin: boolean;
  isUserMember: boolean;
  isUserPending: boolean;
  isUserRejected: boolean;
  screeningQuestionsFormId?: string;
  requestScreeningQuestions?: boolean;
}

export interface CreateOrganizationMutationData {
  name: string;
  description: string;
  badgeImageUrl: string;
  coverImageUrl: string;
  address: string;
}

export interface UpdateOrganizationMutationData {
  groupId: string;
  group: {
    name: string;
    description: string;
    badgeImageUrl: string;
    coverImageUrl: string;
    address: string;
    requestToJoin: boolean;
    permissions: { createPosts: string; createComments: string };
  };
}

export interface UpdateAssociationMutationData {
  associationId: string;
  association: EditAssociationFormValues;
}

export interface UploadExternalContactsMutationData {
  groupId: string;
  contacts: {
    phoneNumber: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  }[];
}

export interface MessageExternalContactsMutationData {
  groupId: string;
  message: string;
}

export interface GetExternalContactsCountDto {
  externalContactsCount: number;
}

export type AssociationMemberDto = {
  id?: string;
  role?: AssociationMemberRole;
  status?: AssociationMemberStatus;
  user: {
    firstName: string;
    id: string;
    lastName: string;
    photoUrl?: string;
  };
};

export type AssociationMembersDto = {
  items: AssociationMemberDto[];
  nextCursor?: string;
  previousCursor?: string;
};

export type AssociationRequestsToPublishDto = {
  currentPage: number;
  pages: number;
  totalResults: number;
  nextCursor?: string;
  previousCursor?: string;
  items: {
    id: string;
    latestPublishRequestDate: string;
    latestPublishRequestFormResponseId: string;
    startDate: string;
    title: string;
    host: {
      firstName: string;
      id: string;
      lastName: string;
      name: string;
      photoUrl?: string;
    };
    groups: {
      address: string;
      associationDependentId: string;
      badgeImageUrl: string;
      coverImageUrl: string;
      description: string;
      id: string;
      name: string;
    }[];
  }[];
};

export type AssociationPublishRequestSortBy =
  | 'startDate'
  | 'host'
  | 'title'
  | 'orgName'
  | 'submissionDate';

export type AssociationEventLogSortBy =
  | 'startDate'
  | 'host'
  | 'title'
  | 'orgName';

export type AssociationEventLogItemPayoutStatus =
  | 'paid'
  | 'pending'
  | 'in_transit'
  | 'canceled'
  | 'failed';

export type AssociationEventLogItemPublicationRequestStatus =
  | 'draft'
  | 'published'
  | 'pending'
  | 'approved'
  | 'rejected';

interface AssociationEventGroup {
  id: string;
  name: string;
  badgeImageUrl: string;
}

export type AssociationEventLogItemDto = {
  eventId: string;
  title?: string;
  host: {
    firstName: string;
    id: string;
    lastName: string;
    name: string;
    photoUrl?: string;
  };
  organizations: AssociationEventGroup[];
  startDate?: Date;
  payoutStatus?: AssociationEventLogItemPayoutStatus;
  payoutAmount?: number;
  publicationRequestStatus?: AssociationEventLogItemPublicationRequestStatus;
  publicationRequestStatusDate?: Date;
  publicationRequestMessage?: string;
  latestPublicationRequestFormResponseId?: string;
  seriesId?: string;
  numberOfOccurrences?: number;
  isDraft?: boolean;
};

export type AssociationEventLogDto = {
  items: AssociationEventLogItemDto[];
  currentPage: number;
  previousCursor?: string;
  nextCursor?: string;
  pages: number;
  totalResults: number;
};

export type AssociationAdminTeamsSortBy =
  | 'createdAt'
  | 'createdBy'
  | 'teamName'
  | 'memberCount'
  | 'updatedAt';
export type AssociationAdminWorkflowsSortBy =
  | 'createdAt'
  | 'createdBy'
  | 'workflowName'
  | 'updatedAt';
export type AssociationAdminFormsSortBy =
  | 'createdAt'
  | 'createdBy'
  | 'formName'
  | 'formType'
  | 'status'
  | 'updatedAt';

export interface BaseMomentDto {
  id: string;
  media: MediaDto[];
  caption?: string;
}
export interface MediaDto {
  url: string;
  thumbnail?: string;
  type: string;
}
export interface AlbumDto {
  id: string;
  name: string;
  groupId: string;
  permissions: AlbumPermissionsDto;
  creator: AlbumCreatorDto;
  canDelete: boolean;
  momentsPreview: BaseMomentDto[];
  momentsCount: number;
  createdAt: Date;
  updatedAt: Date;
  media: MediaDto[];
}
export interface CreateAlbumDto {
  name: string;
  permissions: {
    createMoments: 'admin' | 'member';
    commentMoments: 'admin' | 'member';
  };
}
export interface AlbumsDto {
  items: AlbumDto[];
  currentPage: number;
  limit: number;
  totalPages: number;
}
export interface MomentCreatorDto {
  id: string;
  firstName: string;
  lastName: string;
  photoUrl?: string;
}

export interface MomentPermissions {
  canComment: boolean;
  canEdit: boolean;
}
export interface ReactionDto {
  id: string;
  type:
    | 'heart'
    | 'fire'
    | 'money_mouth_face'
    | 'call_me_hand'
    | 'rolling_on_the_floor_laughing'
    | 'party_face';
  creator: ReactionCreator;
}
export type FriendStatusDto =
  | 'me'
  | 'requestReceived'
  | 'friends'
  | 'requestSent'
  | 'requestable';

export interface ReactionCreator {
  id: string;
  firstName: string;
  lastName: string;
  photoUrl?: string;
  friendStatus?: FriendStatusDto;
}
export interface MomentDto extends BaseMomentDto {
  creator: MomentCreatorDto;
  metadata: {
    numberOfComments: number;
    numberOfReactions: number;
    recentReactions: ReactionDto[];
  };
  permissions: MomentPermissions;
  createdAt: Date;
  updatedAt: Date;
}
export interface MomentsDto {
  items: MomentDto[];
  currentPage: number;
  limit: number;
  totalPages: number;
}
export interface AlbumPermissionsDto {
  currentUser: {
    createMoments: boolean;
    commentMoments: boolean;
  };
  createMoments: 'admin' | 'member';
  commentMoments: 'admin' | 'member';
}

export interface AlbumCreatorDto {
  id: string;
  firstName: string;
  lastName: string;
}
export type AssociationMemberRole = 'admin' | 'member';

export type AssociationMemberStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'verifying';

export interface AssociationAnalyticsSummaryDto {
  totalMembers: number;
  totalRevenue: number;
  totalEvents: number;
  totalOrganizations: number;
  totalTicketsSold: number;
  avgSafetyRating?: number;
  safetyRatingSubmissionCount?: number;
}

export interface GroupAnalyticsSummaryDto {
  totalMembers: number;
  totalRevenue: number;
  totalEvents: number;
  totalTicketsSold: number;
  avgSafetyRating?: number;
  safetyRatingSubmissionCount?: number;
}

interface AnalyticsRevenueAllTimeDataPointDto {
  date: Date;
  revenue: number;
}

export interface AnalyticsRevenueAllTimeDto {
  data: AnalyticsRevenueAllTimeDataPointDto[];
  totalRevenue: number;
}

interface AnalyticsMonthlyDataPointDto {
  date: Date;
  total: number;
}

export interface AnalyticsMonthlyDto {
  data: AnalyticsMonthlyDataPointDto[];
  total: number;
}
